<template>
  <section ref="container" class="uk-flex uk-flex-column uk-flex-middle">
    <error-page v-if="errorType != null" :errorType="errorType" :law="law" :brand="brand"/>

    <div class="uk-card uk-card-default uk-width-1-1 uk-width-3-4@m">
      <identification-page
        :identification-url="identificationUrl"
        :law="law"
        :brand="brand"
        v-if="currentPage == 'identification'"
        @complete="onIdentified"
        @error="showError"
        @willSubmit="clearError"
      />

      <request-page
        v-if="currentPage == 'request'"
        :law="law"
        :theme="theme"
        :requestType="requestType"
        :requestUUID="requestUUID"
        :client="client"
        @complete="onRequestComplete"
        @error="showError"
        @deliveryDataMismatch="onDeliveryDataMismatch"
      />

      <finish
        :requestType="requestType"
        :deliveryMethod="deliveryMethod"
        v-if="currentPage == 'finish'"
      />
    </div>
  </section>
</template>

<script>
import IdentificationPage from "./IdentificationPage";
import RequestPage from "./RequestPage";
import Finish from "./Finish";
import ErrorPage from "./ErrorPage";

import createDcsClient from "../utils/client";

import * as sentry from "@sentry/vue";
import { datadogRum } from "@datadog/browser-rum";

const pages = ["identification", "request", "finish"];
const currentPage = pages[0];

function formatIdentificationError(status) {
  const replaced = status.replace(/_/g, '-');
    return `error-${replaced}`;
}

function determineRequestType(purpose) {
  const purposeReplaced = purpose.replace("data-", "")
  return purposeReplaced.replace(/-/g, '_');
}

export default {
  name: "flow",
  components: {
    IdentificationPage,
    RequestPage,
    Finish,
    ErrorPage
  },
  props: {
    identificationUrl: String,
    brand: String,
    sessionToken: String,
    theme: String,
    law: String,
  },
  data: function() {
    return {
      currentPage: currentPage,
      requestType: null,
      deliveryMethod: null,
      errorType: null,
      token: null,
      requestUUID: null
    };
  },
  mounted: function() {
    if (this.sessionToken != null) {
      this.token = this.sessionToken;
      this.onTokenRetrieved();
    }
  },
  methods: {
    onTokenRetrieved: async function() {
      this.client = createDcsClient(this.token);

      const { session } = await this.client.get("sessions");

      if (session.status == "can_request") {
        const requestType = determineRequestType(session.purpose);
        const { request } = await this.client.post("requests", {
          request_type: requestType
        });

        if (request.status == "error") {
          this.showError(formatIdentificationError(request.error));
        } else {
          this.errorType = null;
          this.requestUUID = request.id;
          this.currentPage = "request";
          this.requestType = requestType;
        }
      } else {
        this.showError(
          formatIdentificationError(session.status)
        );
      }
    },
    onIdentified: async function(response) {
      try {
        this.token = response.session.token;
        datadogRum.addAction("DataCompliance.Flow.OnIdentified.Success");
      } catch (error) {
        sentry.captureMessage(`${error}`, { level: "error", extra: { stack: error.stack } });
        datadogRum.addAction("DataCompliance.Flow.OnIdentified.Error", { error: `${error}`, stack: error.stack });

        return this.showError("server-error");
      }

      return this.onTokenRetrieved();
    },
    onDeliveryDataMismatch: function() {
      this.currentPage = "identification";
      this.showError("delivery-data-mismatch");
    },
    onRequestComplete: function(deliveryMethod) {
      this.errorType = null;
      this.deliveryMethod = deliveryMethod;
      this.currentPage = "finish";

      const event = new Event(`ccpa:form:complete`, { bubbles: true });
      this.$refs.container.dispatchEvent(event);
    },
    showError: function(error) {
      this.errorType = error;
      this.$refs.container.scrollIntoView(true);
    },
    clearError: function() {
      this.errorType = null;
    }
  }
};
</script>

<style scoped></style>
